
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Award, 
  Settings, 
  Download, 
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Users,
  Filter
} from 'lucide-react';
import { AdminCertificateService } from '@/services/adminCertificateService';
import { toast } from '@/components/ui/sonner';

interface BulkCertificateActionsProps {
  pendingCertificates: any[];
  onRefresh: () => void;
}

const BulkCertificateActions: React.FC<BulkCertificateActionsProps> = ({
  pendingCertificates,
  onRefresh
}) => {
  const [processing, setProcessing] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<string>('all');
  const [passThreshold, setPassThreshold] = useState<number>(85);

  const eligibleCertificates = pendingCertificates.filter(cert => cert.canIssue);
  const uniqueCourses = Array.from(new Set(pendingCertificates.map(cert => cert.courseId)));

  const filteredCertificates = selectedCourse === 'all' 
    ? eligibleCertificates 
    : eligibleCertificates.filter(cert => cert.courseId === selectedCourse);

  const handleBulkGenerate = async () => {
    if (filteredCertificates.length === 0) {
      toast.warning('No eligible certificates to generate');
      return;
    }

    setProcessing(true);
    try {
      const result = await AdminCertificateService.bulkGenerateCertificates(filteredCertificates);
      
      if (result.success > 0) {
        toast.success(`Successfully generated ${result.success} certificates`);
        onRefresh();
      }
      
      if (result.failed > 0) {
        toast.warning(`${result.failed} certificates failed to generate`);
        console.log('Failed certificates:', result.errors);
      }
    } catch (error) {
      console.error('Error in bulk generation:', error);
      toast.error('Failed to generate certificates');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateThreshold = async () => {
    if (passThreshold < 70 || passThreshold > 100) {
      toast.error('Pass threshold must be between 70% and 100%');
      return;
    }

    setProcessing(true);
    try {
      // In a real implementation, this would update the threshold in the database
      await AdminCertificateService.setCoursePassThreshold(selectedCourse, passThreshold);
      toast.success(`Pass threshold updated to ${passThreshold}%`);
      onRefresh();
    } catch (error) {
      console.error('Error updating threshold:', error);
      toast.error('Failed to update pass threshold');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Bulk Generation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Bulk Certificate Generation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium">Filter by Course</label>
              <select
                value={selectedCourse}
                onChange={(e) => setSelectedCourse(e.target.value)}
                className="w-full border rounded-md px-3 py-2 mt-1"
              >
                <option value="all">All Courses ({eligibleCertificates.length} eligible)</option>
                {uniqueCourses.map(courseId => {
                  const courseCount = eligibleCertificates.filter(cert => cert.courseId === courseId).length;
                  return (
                    <option key={courseId} value={courseId}>
                      {courseId.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())} ({courseCount} eligible)
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div>
              <p className="font-medium">Ready to Generate</p>
              <p className="text-sm text-muted-foreground">
                {filteredCertificates.length} certificates meet requirements
              </p>
            </div>
            <Button 
              onClick={handleBulkGenerate}
              disabled={processing || filteredCertificates.length === 0}
              className="flex items-center gap-2"
            >
              {processing ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Award className="h-4 w-4" />
              )}
              Generate All
            </Button>
          </div>

          {filteredCertificates.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Preview:</p>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {filteredCertificates.slice(0, 10).map(cert => (
                  <div key={cert.userId} className="flex items-center justify-between text-sm p-2 bg-background rounded">
                    <span>{cert.userName}</span>
                    <span className="text-muted-foreground">{cert.courseTitle}</span>
                    <Badge variant="outline">{cert.quizAverage.toFixed(1)}%</Badge>
                  </div>
                ))}
                {filteredCertificates.length > 10 && (
                  <p className="text-xs text-muted-foreground text-center">
                    ... and {filteredCertificates.length - 10} more
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pass Threshold Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Pass Threshold Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium">Course</label>
              <select
                value={selectedCourse}
                onChange={(e) => setSelectedCourse(e.target.value)}
                className="w-full border rounded-md px-3 py-2 mt-1"
              >
                <option value="all">Global Default</option>
                {uniqueCourses.map(courseId => (
                  <option key={courseId} value={courseId}>
                    {courseId.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-32">
              <label className="text-sm font-medium">Threshold (%)</label>
              <Input
                type="number"
                min="70"
                max="100"
                value={passThreshold}
                onChange={(e) => setPassThreshold(Number(e.target.value))}
                className="mt-1"
              />
            </div>
            <Button 
              onClick={handleUpdateThreshold}
              disabled={processing}
              variant="outline"
              className="mt-6"
            >
              Update
            </Button>
          </div>

          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">Current Settings</p>
                <p className="text-sm text-blue-700">
                  Pass threshold is set to {passThreshold}% for {selectedCourse === 'all' ? 'all courses' : selectedCourse}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Users must achieve this average across all quiz attempts to earn a certificate
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Health */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            System Health & Monitoring
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <div className="font-medium text-green-900">Certificate Generation</div>
              <div className="text-sm text-green-700">Operating Normally</div>
            </div>
            
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <Users className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <div className="font-medium text-blue-900">Course Completion Tracking</div>
              <div className="text-sm text-blue-700">Active Monitoring</div>
            </div>
            
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <Award className="h-8 w-8 text-purple-600 mx-auto mb-2" />
              <div className="font-medium text-purple-900">Quality Assurance</div>
              <div className="text-sm text-purple-700">85%+ Average Maintained</div>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div>
              <p className="font-medium">Manual System Check</p>
              <p className="text-sm text-muted-foreground">
                Refresh certificate data and check for any missed completions
              </p>
            </div>
            <Button 
              onClick={onRefresh}
              disabled={processing}
              variant="outline"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh Data
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BulkCertificateActions;
